import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  createKnowledgeBaseGrant$,
  listKnowledgeBaseGrantDependents$,
  listKnowledgeBaseGrantsForKnowledgeBase$,
  revokeKnowledgeBaseGrants$,
} from '@vendasta/protos/embeddings/v1alpha1/knowledge-base-grant-service';
import type {
  KnowledgeBaseGrant as ProtoKnowledgeBaseGrant,
  KnowledgeBaseGrantSpec,
  DependentSummary as ProtoDependentSummary,
  Namespace as ProtoNamespace,
} from '@vendasta/protos/embeddings/v1alpha1/types';
import type { TargetScope as ProtoTargetScope } from '@vendasta/protos/embeddings/v1alpha1/enums';

import { PARTNER_ID_TOKEN, ACCOUNT_GROUP_ID_TOKEN } from '../tokens';
import { VENDASTA_PARTNER_ID } from '../services/ai-knowledge.service';
import {
  GrantDependentSummary,
  KnowledgeBaseGrant,
  Namespace,
  RevokeResult,
  TargetNamespaceType,
  TargetOption,
  TargetScope,
  targetIdentityKey,
} from './types';

/**
 * Wraps the generated `KnowledgeBaseGrantService` RPCs and translates between proto messages and the UI types in `./types`, keeping the components proto-agnostic.
 *
 * The UI's `grantId` is the proto `lookupKey` (the proto grant has no id field); a `lookupKey -> ProtoGrant` map lets revoke rebuild the tuple the RPC needs. `revokeGrant` returns a discriminated `RevokeResult` instead of throwing on the dependents-present path (the BE's non-cascading revoke contract).
 *
 * `removeKnowledgeBaseFromAppsInNamespace` is still a stub — that RPC isn't in the codegen yet.
 */
@Injectable({ providedIn: 'root' })
export class KnowledgeBaseGrantSdkService {
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);

  /** Proto grants by lookupKey; revoke uses these to rebuild the (issuer, target) tuple. */
  private readonly protoGrantsByLookupKey = new Map<string, ProtoKnowledgeBaseGrant>();

  async listGrantsForKB(kbId: string): Promise<KnowledgeBaseGrant[]> {
    const callerNs = await this.buildCallerNamespace();
    const resp = await firstValueFrom(
      listKnowledgeBaseGrantsForKnowledgeBase$({
        sourceKb: { knowledgeBaseId: kbId, namespace: callerNs },
        pagingOptions: { pageSize: 100 },
      }),
    );
    const grants = resp.grants ?? [];
    this.protoGrantsByLookupKey.clear();
    for (const g of grants) {
      if (g.lookupKey) this.protoGrantsByLookupKey.set(g.lookupKey, g);
    }
    return grants.map((g) => toUIGrant(g));
  }

  async createGrant(kbId: string, target: TargetOption): Promise<KnowledgeBaseGrant> {
    const callerNs = await this.buildCallerNamespace();
    const spec: Partial<KnowledgeBaseGrantSpec> = {
      sourceKb: { knowledgeBaseId: kbId, namespace: callerNs as ProtoNamespace },
      targetNamespace: targetOptionToProtoNamespace(target),
      targetScope: uiScopeToProtoScope(target.scope),
      grantedByNamespace: callerNs as ProtoNamespace,
    };
    const resp = await firstValueFrom(createKnowledgeBaseGrant$({ grant: spec }));
    const grant = resp.grant;
    if (!grant) {
      throw new Error('Failed to create grant: no grant returned');
    }
    if (grant.lookupKey) {
      this.protoGrantsByLookupKey.set(grant.lookupKey, grant);
    }
    return toUIGrant(grant);
  }

  /** Looks up the proto grant by lookupKey (the UI's `grantId`) to build the (sourceKb, issuer, target) tuple the RPC needs. */
  async revokeGrant(_kbId: string, grantId: string): Promise<RevokeResult> {
    void _kbId; // kept on the signature for the existing dialog call shape
    const proto = this.protoGrantsByLookupKey.get(grantId);
    if (!proto) {
      // Already revoked (or list was never primed). Idempotent.
      return { ok: true };
    }
    const resp = await firstValueFrom(
      revokeKnowledgeBaseGrants$({
        sourceKb: proto.sourceKb,
        issuerNamespace: proto.grantedByNamespace,
        targetNamespace: proto.targetNamespace,
        targetScope: proto.targetScope,
      }),
    );
    const result = resp.results?.[0];
    // The BE returns FAILED_PRECONDITION with populated `dependents` when the revoke is blocked.
    if (result && isFailedPrecondition(result.statusCode) && result.dependents) {
      return { ok: false, dependents: toUISummary(result.dependents) };
    }
    this.protoGrantsByLookupKey.delete(grantId);
    return { ok: true };
  }

  async listGrantDependents(
    kbId: string,
    grantedByNamespace: Namespace,
    targetNamespace?: Namespace,
  ): Promise<GrantDependentSummary> {
    if (!targetNamespace) return emptySummary();
    const callerNs = await this.buildCallerNamespace();
    const resp = await firstValueFrom(
      listKnowledgeBaseGrantDependents$({
        sourceKb: { knowledgeBaseId: kbId, namespace: callerNs as ProtoNamespace },
        issuerNamespace: uiNamespaceToProto(grantedByNamespace),
        targetNamespace: uiNamespaceToProto(targetNamespace),
        pagingOptions: { pageSize: 100 },
      }),
    );
    let downstreamCount = 0;
    let appCount = 0;
    const sampleDownstream: string[] = [];
    const sampleApps: string[] = [];
    for (const item of resp.grants ?? []) {
      const d = item.dependents;
      if (!d) continue;
      downstreamCount += d.downstreamGrants?.length ?? 0;
      appCount += d.appAssociations?.length ?? 0;
      for (const dg of d.downstreamGrants?.slice(0, 3) ?? []) {
        const label = describeNamespace(dg.targetNamespace);
        if (label) sampleDownstream.push(label);
      }
      for (const aa of d.appAssociations?.slice(0, 3) ?? []) {
        const id = (aa as { appId?: string }).appId;
        if (id) sampleApps.push(id);
      }
    }
    return {
      downstreamGrantCount: downstreamCount,
      appAssociationCount: appCount,
      sampleDownstreamGrants: sampleDownstream.slice(0, 5),
      sampleAppAssociations: sampleApps.slice(0, 5),
    };
  }

  /** Revokes every grant on `sourceKbId` issued from `grantedByNamespace` (the RPC is per-tuple, so list-filter-revoke). */
  async revokeIssuedGrants(sourceKbId: string, grantedByNamespace: Namespace): Promise<void> {
    const callerNs = await this.buildCallerNamespace();
    const resp = await firstValueFrom(
      listKnowledgeBaseGrantsForKnowledgeBase$({
        sourceKb: { knowledgeBaseId: sourceKbId, namespace: callerNs as ProtoNamespace },
        pagingOptions: { pageSize: 100 },
      }),
    );
    const protoIssuer = uiNamespaceToProto(grantedByNamespace);
    const matches = (resp.grants ?? []).filter((g) =>
      protoNamespacesMatch(g.grantedByNamespace, protoIssuer),
    );
    for (const g of matches) {
      await firstValueFrom(
        revokeKnowledgeBaseGrants$({
          sourceKb: g.sourceKb,
          issuerNamespace: g.grantedByNamespace,
          targetNamespace: g.targetNamespace,
          targetScope: g.targetScope,
        }),
      );
      if (g.lookupKey) this.protoGrantsByLookupKey.delete(g.lookupKey);
    }
  }

  /** STUB(DALE-1279-followup): `RemoveKnowledgeBaseFromAppsInNamespace` isn't in the codegen yet — swap for the real call when it lands. */
  async removeKnowledgeBaseFromAppsInNamespace(kbId: string, appNamespace: Namespace): Promise<void> {
    void kbId;
    void appNamespace;
    // No-op; the BE-side dependents check is what gates revoke success.
  }

  /** Build the proto Namespace for the current caller from injected tokens. */
  private async buildCallerNamespace(): Promise<Partial<ProtoNamespace>> {
    const agId = await firstValueFrom(this.accountGroupId$).catch(() => '');
    if (agId) {
      return { accountGroupNamespace: { accountGroupId: agId } } satisfies Partial<ProtoNamespace>;
    }
    const partnerId = await firstValueFrom(this.partnerId$).catch(() => '');
    // Mirror AiKnowledgeService.buildNamespaceInterface: the Vendasta house partner (VA) maps to the
    // admin/global namespace, so its KBs/sources/grants must all key off adminNamespace — not
    // partnerNamespace:VA — or grants won't line up with the KBs they reference.
    // TODO(DALE-1279-followup): untangle this — drive the namespace by surface (Admin Center → admin,
    // Partner Center → partner) instead of special-casing the VA partner id.
    if (partnerId && partnerId !== VENDASTA_PARTNER_ID) {
      return { partnerNamespace: { partnerId } } satisfies Partial<ProtoNamespace>;
    }
    return { adminNamespace: {} } satisfies Partial<ProtoNamespace>;
  }
}

// ─── Proto ↔ UI translation helpers ────────────────────────────────────────

function toUIGrant(proto: ProtoKnowledgeBaseGrant): KnowledgeBaseGrant {
  const target = protoNamespaceToTargetOption(proto.targetNamespace, proto.targetScope);
  return {
    grantId: proto.lookupKey ?? target.identityKey,
    sourceKBId: proto.sourceKb?.knowledgeBaseId ?? '',
    target,
    grantedByUserId: proto.grantedByUserId ?? '',
    created: proto.created ?? new Date(),
  };
}

function toUISummary(proto: ProtoDependentSummary): GrantDependentSummary {
  return {
    downstreamGrantCount: proto.downstreamGrants?.length ?? 0,
    appAssociationCount: proto.appAssociations?.length ?? 0,
    sampleDownstreamGrants: (proto.downstreamGrants ?? [])
      .slice(0, 5)
      .map((g) => describeNamespace(g.targetNamespace))
      .filter((s): s is string => Boolean(s)),
    sampleAppAssociations: (proto.appAssociations ?? [])
      .slice(0, 5)
      .map((a) => (a as { appId?: string }).appId ?? '')
      .filter(Boolean),
  };
}

function emptySummary(): GrantDependentSummary {
  return { downstreamGrantCount: 0, appAssociationCount: 0 };
}

/** Maps proto Namespace + TargetScope back into the UI's TargetOption. */
function protoNamespaceToTargetOption(
  ns: ProtoNamespace | undefined,
  protoScope: ProtoTargetScope | undefined,
): TargetOption {
  const uiScope = protoScopeToUIScope(protoScope);
  const { type, id, displayName } = readProtoNamespace(ns);
  return {
    identityKey: targetIdentityKey(type, id, uiScope),
    namespaceId: id,
    namespaceType: type,
    scope: uiScope,
    displayName,
  };
}

function readProtoNamespace(
  ns: ProtoNamespace | undefined,
): { type: TargetNamespaceType; id: string; displayName: string } {
  if (!ns) return { type: 'Global', id: '*', displayName: 'Unknown' };
  if (ns.accountGroupNamespace?.accountGroupId) {
    const id = ns.accountGroupNamespace.accountGroupId;
    return { type: 'AccountGroup', id, displayName: id };
  }
  if (ns.partnerNamespace?.partnerId) {
    const id = ns.partnerNamespace.partnerId;
    return { type: 'Partner', id, displayName: id };
  }
  if (ns.multiLocationGroupNamespace?.multiLocationGroupId) {
    const id = ns.multiLocationGroupNamespace.multiLocationGroupId;
    return { type: 'MultiLocationGroup', id, displayName: id };
  }
  return { type: 'Global', id: '*', displayName: 'Global' };
}

function describeNamespace(ns: ProtoNamespace | undefined): string | undefined {
  if (!ns) return undefined;
  const { type, id } = readProtoNamespace(ns);
  return `${type}:${id}`;
}

function targetOptionToProtoNamespace(target: TargetOption): Partial<ProtoNamespace> {
  return uiNamespaceToProto({ id: target.namespaceId, type: target.namespaceType });
}

function uiNamespaceToProto(ns: Namespace): Partial<ProtoNamespace> {
  switch (ns.type) {
    case 'AccountGroup':
      return { accountGroupNamespace: { accountGroupId: ns.id } } satisfies Partial<ProtoNamespace>;
    case 'Partner':
      return { partnerNamespace: { partnerId: ns.id } } satisfies Partial<ProtoNamespace>;
    case 'MultiLocationGroup':
      return {
        multiLocationGroupNamespace: { multiLocationGroupId: ns.id },
      } satisfies Partial<ProtoNamespace>;
    case 'Global':
      return { adminNamespace: {} } satisfies Partial<ProtoNamespace>;
  }
}

function protoNamespacesMatch(
  a: ProtoNamespace | undefined,
  b: Partial<ProtoNamespace> | undefined,
): boolean {
  if (!a || !b) return false;
  const ar = readProtoNamespace(a);
  const br = readProtoNamespace(b as ProtoNamespace);
  return ar.type === br.type && ar.id === br.id;
}

// UI 'descendants' folds into 'ALL_AGS' — the proto's only descendants semantic applies to AG-trees.
function uiScopeToProtoScope(uiScope: TargetScope): ProtoTargetScope {
  switch (uiScope) {
    case 'self': return 'TARGET_SCOPE_SELF';
    case 'descendants': return 'TARGET_SCOPE_ALL_AGS';
    case 'all_ags': return 'TARGET_SCOPE_ALL_AGS';
    case 'all_partners': return 'TARGET_SCOPE_ALL_PARTNERS';
  }
}

function protoScopeToUIScope(protoScope: ProtoTargetScope | string | undefined): TargetScope {
  switch (protoScope) {
    case 'TARGET_SCOPE_SELF': return 'self';
    case 'TARGET_SCOPE_ALL_AGS': return 'all_ags';
    case 'TARGET_SCOPE_ALL_PARTNERS': return 'all_partners';
    default: return 'self';
  }
}

/** FAILED_PRECONDITION = gRPC Code 9; matched loosely since codegen may emit the numeric or string form. */
function isFailedPrecondition(statusCode: unknown): boolean {
  return statusCode === 9 || statusCode === 'FAILED_PRECONDITION';
}
